// Helpers de consolidação jurídica (cível/criminal) para o Dashboard Executivo

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const resolvePropertiesRoot = (data) => {
  if (!data || typeof data !== 'object') return {};

  if (data.gn_properties && typeof data.gn_properties === 'object') {
    return data.gn_properties;
  }

  if (data.properties && typeof data.properties === 'object') {
    return data.properties;
  }

  if (data.data && typeof data.data === 'object') {
    if (data.data.gn_properties && typeof data.data.gn_properties === 'object') {
      return data.data.gn_properties;
    }
    if (data.data.properties && typeof data.data.properties === 'object') {
      return data.data.properties;
    }
  }

  return {};
};

const extractProcessesFromStatusNode = (statusNode) => {
  if (!statusNode) return [];

  // Formato agregado atual: statusNode = [{ cpf, nome, processos: [...] }, ...]
  if (Array.isArray(statusNode)) {
    const out = [];
    statusNode.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const processos = Array.isArray(entry.processos) ? entry.processos : [];
      processos.forEach((p) => {
        if (p && typeof p === 'object') out.push(p);
      });
    });
    return out;
  }

  // Formato legado: statusNode = { processos: [...] }
  if (typeof statusNode === 'object' && Array.isArray(statusNode.processos)) {
    return statusNode.processos.filter((p) => p && typeof p === 'object');
  }

  return [];
};

export const getLegalProcessSources = (data) => {
  const properties = resolvePropertiesRoot(data);
  const legalRoot = properties.legal;

  const legalList = Array.isArray(legalRoot)
    ? legalRoot.filter((item) => item && typeof item === 'object')
    : legalRoot && typeof legalRoot === 'object'
      ? [legalRoot]
      : [];

  const legacyCivilStatus = data?.legalstatus || properties.legalstatus;
  const legacyCriminalStatus = data?.criminalstatus || properties.criminalstatus;

  const civil = [];
  const criminal = [];

  if (legacyCivilStatus) {
    civil.push(...extractProcessesFromStatusNode(legacyCivilStatus));
  }
  if (legacyCriminalStatus) {
    criminal.push(...extractProcessesFromStatusNode(legacyCriminalStatus));
  }

  legalList.forEach((legalObj) => {
    if (!legalObj || typeof legalObj !== 'object') return;
    civil.push(...extractProcessesFromStatusNode(legalObj.civilstatus || legalObj.civil));
    criminal.push(...extractProcessesFromStatusNode(legalObj.criminalstatus || legalObj.criminal));
  });

  return {
    civil,
    criminal,
  };
};

const classifyProcessStatusBucket = (processo) => {
  const inferido = String(processo?.status?.inferido || '').toLowerCase();
  const tribunalStatus = String(processo?.status?.tribunal || '').toLowerCase();
  const combined = `${inferido} ${tribunalStatus}`;

  if (combined.includes('ativo')) return 'ativo';
  if (combined.includes('arquiv')) return 'arquivado';
  return 'inativo';
};

const summarizeProcesses = (processes) => {
  if (!Array.isArray(processes) || processes.length === 0) {
    return {
      totalCount: 0,
      valorLitigio: 0,
      ativos: 0,
      arquivados: 0,
      inativos: 0,
    };
  }

  let totalCount = 0;
  let valorLitigio = 0;
  let ativos = 0;
  let arquivados = 0;
  let inativos = 0;

  processes.forEach((p) => {
    if (!p || typeof p !== 'object') return;

    totalCount += 1;

    const v = safeNumber(p.valor_causa);
    if (v !== null) {
      valorLitigio += v;
    }

    const bucket = classifyProcessStatusBucket(p);
    if (bucket === 'ativo') ativos += 1;
    else if (bucket === 'arquivado') arquivados += 1;
    else inativos += 1;
  });

  return {
    totalCount,
    valorLitigio,
    ativos,
    arquivados,
    inativos,
  };
};

export function getLegalDashboardData(data) {
  const { civil, criminal } = getLegalProcessSources(data || {});

  const civilSummary = summarizeProcesses(civil);
  const criminalSummary = summarizeProcesses(criminal);

  return {
    civil: civilSummary,
    criminal: criminalSummary,
  };
}

// Compatibilidade retroativa com nome anterior
export const getLegalProcessSourcesForDashboard = getLegalProcessSources;
